import { getEnv } from "./env";
import { authClient } from "../authClient";

let currentAccessToken: string | null = null;
let currentOrgId: string | null = null;

export const setAuthToken = (token: string | null, orgId: string | null = null) => {
  currentAccessToken = token;
  currentOrgId = orgId;
};

export const getApiUrl = (): string => {
  return getEnv("VITE_API_URL", "").replace(/\/+$/, "");
};

export const customFetch = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const baseUrl = getApiUrl();
  let fullUrl: string;

  if (url.startsWith("http://") || url.startsWith("https://")) {
    fullUrl = url;
  } else {
    let path = url.startsWith("/") ? url : `/${url}`;
    if (baseUrl.endsWith("/api") && (path === "/api" || path.startsWith("/api/"))) {
      path = path.slice(4);
    }
    fullUrl = `${baseUrl}${path}`;
  }

  const headers = new Headers(options.headers || {});

  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // Ensure access token is present
  let token = currentAccessToken;
  if (!token && !headers.has("Authorization")) {
    try {
      const authInfo = await authClient.getAuthenticationInfoOrNull();
      if (authInfo?.accessToken) {
        token = authInfo.accessToken;
        currentAccessToken = token;
      }
    } catch {
      // ignore
    }
  }

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Ensure active Org ID is present
  let orgId = currentOrgId;
  if (!orgId && !headers.has("X-Org-Id")) {
    try {
      orgId = localStorage.getItem("__last_selected_org");
    } catch {
      // ignore
    }
  }

  if (orgId && !headers.has("X-Org-Id")) {
    headers.set("X-Org-Id", orgId);
  }

  let response: Response;
  const useMock = getEnv("VITE_USE_MOCK", "").toLowerCase() === "true";

  if (!useMock) {
    try {
      response = await fetch(fullUrl, {
        ...options,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401 && typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("auth:unauthorized", {
              detail: {
                status: 401,
                url: fullUrl,
              },
            })
          );
        }
        let errorDetail = `Request failed with status ${response.status}`;
        try {
          const errJson = await response.json();
          if (errJson && errJson.detail) {
            errorDetail = typeof errJson.detail === "string" ? errJson.detail : JSON.stringify(errJson.detail);
          }
        } catch {
          // ignore json parse error
        }
        throw new Error(errorDetail);
      }

      let data: any;
      if (response.status === 204) {
        data = {};
      } else {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          data = await response.json();
        } else {
          data = await response.text();
        }
      }

      return {
        data,
        status: response.status,
        headers: response.headers,
      } as unknown as T;
    } catch (networkError: any) {
      // If error was explicitly from API status != 200 (like 404 or 422), rethrow
      if (networkError.message && networkError.message.startsWith("Request failed with status")) {
        throw networkError;
      }
      // If server unreachable, fallback to mock API
    }
  }

  // Fallback to mock API
  const { mockApi } = await import("../mocks/handlers");
  const parsedUrl = new URL(url, "http://localhost");
  const pathname = parsedUrl.pathname;
  const method = (options.method || "GET").toUpperCase();
  const searchParams = parsedUrl.searchParams;
  const getParam = (k: string) => searchParams.get(k) || undefined;
  const body = options.body ? JSON.parse(options.body as string) : {};

  let mockData: any = null;

  // Threads
  if (pathname.includes("/api/v1/threads")) {
    const msgMatch = pathname.match(/\/api\/v1\/threads\/([^\/]+)\/messages/);
    if (msgMatch) {
      const threadId = msgMatch[1];
      if (method === "GET") mockData = await mockApi.getMessages(threadId);
      else if (method === "POST") mockData = await mockApi.appendMessage(threadId, body);
    } else {
      const threadMatch = pathname.match(/\/api\/v1\/threads\/([^\/]+)$/);
      if (threadMatch && threadMatch[1] !== "threads") {
        mockData = await mockApi.getThread(threadMatch[1]);
      } else {
        if (method === "POST") {
          mockData = await mockApi.createThread(body);
        } else {
          mockData = await mockApi.listThreads({
            agent_type: getParam("agent_type"),
            status: getParam("status"),
          });
        }
      }
    }
  }
  // Users
  else if (pathname.includes("/api/v1/users")) {
    const isAgentParam = getParam("is_agent");
    mockData = await mockApi.listUsers({
      is_agent: isAgentParam !== undefined ? isAgentParam === "true" : undefined,
      agent_type: getParam("agent_type"),
      status: getParam("status"),
    });
  }
  // Runs
  else if (pathname.includes("/api/v1/runs")) {
    const eventsMatch = pathname.match(/\/api\/v1\/runs\/([^\/]+)\/events/);
    if (eventsMatch) {
      mockData = await mockApi.getRunEvents(eventsMatch[1]);
    } else {
      const runMatch = pathname.match(/\/api\/v1\/runs\/([^\/]+)$/);
      if (runMatch && runMatch[1] !== "runs") {
        mockData = await mockApi.getRun(runMatch[1]);
      } else {
        mockData = await mockApi.listRuns({
          thread_id: getParam("thread_id"),
          agent_type: getParam("agent_type"),
          status: getParam("status"),
        });
      }
    }
  }
  // Feedback
  else if (pathname.includes("/api/v1/feedback")) {
    if (method === "POST") {
      mockData = await mockApi.recordFeedback(body);
    } else {
      mockData = await mockApi.listFeedback({
        target_id: getParam("target_id"),
        target_type: getParam("target_type"),
      });
    }
  }
  // Rules
  else if (pathname.includes("/api/v1/rules")) {
    if (pathname.endsWith("/active")) {
      const allRules = await mockApi.listRules({ agent_type: body.agent_type, status: "active" });
      mockData = { rules: allRules };
    } else if (pathname.endsWith("/history")) {
      const id = pathname.split("/").slice(-2)[0];
      mockData = await mockApi.getRuleHistory(id);
    } else if (pathname.endsWith("/activate")) {
      const id = pathname.split("/").slice(-2)[0];
      mockData = await mockApi.activateRule(id);
    } else if (pathname.endsWith("/supersede")) {
      const id = pathname.split("/").slice(-2)[0];
      mockData = await mockApi.supersedeRule(id, body);
    } else if (pathname.endsWith("/deprecate")) {
      const id = pathname.split("/").slice(-2)[0];
      mockData = await mockApi.deprecateRule(id);
    } else if (pathname.endsWith("/draft") && method === "POST") {
      mockData = await mockApi.createRuleDraft(body);
    } else {
      const ruleMatch = pathname.match(/\/api\/v1\/rules\/([^\/]+)$/);
      if (ruleMatch && ruleMatch[1] !== "rules") {
        if (method === "PUT") mockData = await mockApi.updateRule(ruleMatch[1], body);
        else mockData = await mockApi.getRule(ruleMatch[1]);
      } else {
        mockData = await mockApi.listRules({
          agent_type: getParam("agent_type"),
          status: getParam("status"),
          category: getParam("category"),
        });
      }
    }
  }
  // Knowledge
  else if (pathname.includes("/api/v1/knowledge")) {
    if (pathname.includes("/chunks")) {
      const id = pathname.match(/\/api\/v1\/knowledge\/items\/([^\/]+)\/chunks/)?.[1];
      mockData = id ? await mockApi.getKnowledgeChunks(id) : [];
    } else if (pathname.includes("/rollback")) {
      const id = pathname.match(/\/api\/v1\/knowledge\/items\/([^\/]+)\/rollback/)?.[1];
      mockData = id ? await mockApi.rollbackKnowledgeItem(id, body?.reason) : {};
    } else {
      const itemMatch = pathname.match(/\/api\/v1\/knowledge\/items\/([^\/]+)$/);
      if (itemMatch && itemMatch[1] !== "items") {
        if (method === "PUT") mockData = await mockApi.updateKnowledgeItem(itemMatch[1], body);
        else mockData = await mockApi.getKnowledgeItem(itemMatch[1]);
      } else {
        if (method === "POST") mockData = await mockApi.createKnowledgeItem(body);
        else {
          mockData = await mockApi.listKnowledgeItems({
            category: getParam("category"),
            agent_type: getParam("agent_type"),
          });
        }
      }
    }
  }
  // Views
  else if (pathname.includes("/api/v1/views")) {
    const setDefaultMatch = pathname.match(/\/api\/v1\/views\/([^\/]+)\/set-default$/);
    const clearDefaultMatch = pathname.match(/\/api\/v1\/views\/([^\/]+)\/clear-default$/);
    if (setDefaultMatch) {
      mockData = await mockApi.setDefaultView(setDefaultMatch[1]);
    } else if (clearDefaultMatch) {
      mockData = await mockApi.clearDefaultView(clearDefaultMatch[1]);
    } else {
      const viewMatch = pathname.match(/\/api\/v1\/views\/([^\/]+)$/);
      if (viewMatch && viewMatch[1] !== "views") {
        const viewId = viewMatch[1];
        if (method === "GET") mockData = await mockApi.getView(viewId);
        else if (method === "PATCH" || method === "PUT") mockData = await mockApi.updateView(viewId, body);
        else if (method === "DELETE") mockData = await mockApi.deleteView(viewId);
      } else {
        if (method === "POST") mockData = await mockApi.createView(body);
        else mockData = await mockApi.listViews();
      }
    }
  } else {
    mockData = {};
  }

  return {
    data: mockData,
    status: 200,
    headers: new Headers(),
  } as unknown as T;
};

export default customFetch;

